import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, ButtonGroup, Form, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "../crmpostventa-v2/crmPostventaV2Api";
import { openAssignResponsibleDialog } from "../crmpostventa-v2/assignResponsibleDialog";
import ProcessActionModal from "../crmpostventa-v2/components/ProcessActionModal";
import ProcessQueueTable from "../crmpostventa-v2/components/ProcessQueueTable";
import { CRM_POSTVENTA_V2_SECTIONS, getSlugByProcessCode } from "../crmpostventa-v2/processCatalog";
import "../crmpostventa-v2/CRMPostventaV2Procesos.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const emptySummary = {
  onboardingPendientes: 0,
  zocoCheckActivos: 0,
  sinFacturacionActivos: 0,
  recuperoActivos: 0,
  extraviadasActivas: 0,
  compliancePendientes: 0,
  proximosContactosVencidos: 0,
  procesosSinResponsable: 0,
  alertasCriticas: 0,
  totalProcesosActivos: 0,
};

const emptyPermissions = {
  puedeVerPostventaGeneral: false,
  puedeGestionarPostventaGeneral: false,
  puedeVerCompliance: false,
  puedeGestionarCompliance: false,
  puedeAdministrarPostventa: false,
};

const isVersionConflict = (message = "") => {
  const normalized = message.toLowerCase();
  return normalized.includes("actualizada")
    || normalized.includes("actualizado")
    || normalized.includes("modifico")
    || normalized.includes("modificada")
    || normalized.includes("rowversion")
    || normalized.includes("concurrency");
};

const priorityOptions = [
  { value: "", label: "Todas las prioridades" },
  { value: "CRITICA", label: "Critica" },
  { value: "ALTA", label: "Alta" },
  { value: "MEDIA", label: "Media" },
  { value: "BAJA", label: "Baja" },
];

const generalStateOptions = [
  { value: "", label: "Todos los estados" },
  { value: "PENDIENTE_CONTACTO_INICIAL", label: "Pendiente de contacto inicial" },
  { value: "PENDIENTE_PRIMER_CONTACTO", label: "Pendiente de contacto inicial" },
  { value: "PENDIENTE_PRIMER_WHATSAPP", label: "Primer WhatsApp" },
  { value: "ESPERANDO_SEGUNDO_WHATSAPP", label: "Segundo WhatsApp pendiente" },
  { value: "ESPERANDO_LLAMADA", label: "Llamada pendiente" },
  { value: "PENDIENTE_WHATSAPP_1", label: "Primer WhatsApp" },
  { value: "PENDIENTE_LLAMADA_1", label: "Primera llamada" },
  { value: "PENDIENTE_WHATSAPP_2", label: "Segundo WhatsApp" },
  { value: "PENDIENTE_LLAMADA_2", label: "Segunda llamada" },
  { value: "PENDIENTE_LLAMADA", label: "Llamada pendiente" },
  { value: "ESPERANDO_NUEVA_ALERTA", label: "Esperando nueva alerta" },
  { value: "REQUIERE_CORRECCION", label: "Requiere correccion" },
  { value: "CERRADO", label: "Cerrado" },
];

const processOptions = [
  { value: "", label: "Todos los procesos" },
  ...CRM_POSTVENTA_V2_SECTIONS.filter((item) => !item.isSpecial).map((item) => ({
    value: item.slug,
    label: item.shortTitle,
  })),
];

const scopeOptions = [
  { value: "ALL", label: "Todas" },
  { value: "MINE", label: "Mis gestiones" },
  { value: "UNASSIGNED", label: "Sin asignar" },
];

const buildDefaultFilters = (complianceMode = false) => ({
  procesoCodigo: "",
  prioridad: "",
  estado: "",
  texto: "",
  responsableUsuarioId: "",
  soloVencidos: false,
  soloCompliance: complianceMode,
  scope: "ALL",
});

const buildRequestFromFilters = (filters) => ({
  procesoCodigo: filters.procesoCodigo || undefined,
  estado: filters.estado || undefined,
  prioridad: filters.prioridad || undefined,
  texto: filters.texto || undefined,
  responsableUsuarioId: filters.responsableUsuarioId ? Number(filters.responsableUsuarioId) : undefined,
  soloVencidos: !!filters.soloVencidos,
  soloCompliance: !!filters.soloCompliance,
  soloMisGestiones: filters.scope === "MINE",
  soloSinResponsable: filters.scope === "UNASSIGNED",
});

export default function CrmPostventaV2AlertCenter({ darkMode = false }) {
  const tableRef = useRef(null);
  const [fechaReferencia, setFechaReferencia] = useState(makeToday());
  const [summary, setSummary] = useState(emptySummary);
  const [counter, setCounter] = useState({ total: 0, nuevas: 0, vencidas: 0, criticas: 0, compliance: 0, refreshSeconds: 60 });
  const [rows, setRows] = useState([]);
  const [responsables, setResponsables] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [permissions, setPermissions] = useState(emptyPermissions);
  const [filters, setFilters] = useState(buildDefaultFilters(false));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const complianceMode = permissions.puedeVerCompliance && !permissions.puedeVerPostventaGeneral;

  const loadData = async (nextFilters = filters, nextDate = fechaReferencia) => {
    setLoading(true);
    try {
      const request = buildRequestFromFilters(nextFilters);
      const [summaryResponse, listResponse, counterResponse] = await Promise.all([
        crmPostventaV2Api.obtenerResumenAlertas({ fechaReferencia: nextDate }),
        crmPostventaV2Api.obtenerAlertas({
          fechaReferencia: nextDate,
          ...request,
          pagina: 1,
          tamanoPagina: 50,
        }),
        crmPostventaV2Api.obtenerContadorAlertas({ fechaReferencia: nextDate }),
      ]);

      const nextPermissions = listResponse?.permisos || emptyPermissions;
      const shouldForceCompliance = !!(nextPermissions.puedeVerCompliance && !nextPermissions.puedeVerPostventaGeneral);
      const normalizedFilters = shouldForceCompliance && !nextFilters.soloCompliance
        ? { ...nextFilters, soloCompliance: true }
        : nextFilters;

      setSummary(summaryResponse || emptySummary);
      setRows(listResponse?.filas || []);
      setResponsables(listResponse?.responsables || []);
      setNotifications(listResponse?.notificaciones || []);
      setPermissions(nextPermissions);
      setCounter(counterResponse || { total: 0, nuevas: 0, vencidas: 0, criticas: 0, compliance: 0, refreshSeconds: 60 });

      if (normalizedFilters !== nextFilters) {
        setFilters(normalizedFilters);
      }
    } catch (error) {
      Swal.fire("CRM Postventa V2", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(filters, fechaReferencia);
  }, []);

  useEffect(() => {
    const seconds = Number(counter?.refreshSeconds || 60);
    if (!Number.isFinite(seconds) || seconds < 15) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      loadData(filters, fechaReferencia);
    }, seconds * 1000);

    return () => window.clearInterval(timer);
  }, [counter?.refreshSeconds, filters, fechaReferencia]);

  const cardItems = useMemo(() => {
    const baseCards = [
      {
        key: "mis-gestiones",
        title: "Mis gestiones",
        value: rows.filter((item) => !!item.responsableUsuarioId).length,
        note: "Filtra procesos tomados por el usuario actual.",
        tone: "primary",
        patch: { scope: "MINE" },
      },
      {
        key: "sin-asignar",
        title: "Sin asignar",
        value: summary.procesosSinResponsable,
        note: "Casos operativos todavia sin responsable.",
        tone: "secondary",
        patch: { scope: "UNASSIGNED" },
      },
      {
        key: "vencidos",
        title: "Vencidos",
        value: summary.proximosContactosVencidos,
        note: "La fecha de proxima accion ya vencio.",
        tone: "danger",
        patch: { soloVencidos: true },
      },
      {
        key: "criticas",
        title: "Criticas",
        value: summary.alertasCriticas,
        note: "Prioridad maxima dentro del centro operativo.",
        tone: "danger",
        patch: { prioridad: "CRITICA" },
      },
    ];

    if (complianceMode) {
      return [
        ...baseCards,
        {
          key: "compliance",
          title: "Derivaciones a Compliance",
          value: summary.compliancePendientes,
          note: "Visible en solo lectura. La gestion seguira en el futuro modulo Compliance.",
          tone: "warning",
          patch: { soloCompliance: true, scope: "ALL", procesoCodigo: "", estado: "", prioridad: "", responsableUsuarioId: "" },
        },
      ];
    }

    return [
      ...baseCards,
      {
        key: "onboarding",
        title: "Onboarding",
        value: summary.onboardingPendientes,
        note: "Altas que requieren contacto inicial.",
        tone: "info",
        patch: { procesoCodigo: "onboarding", soloCompliance: false },
      },
      {
        key: "zoco-check",
        title: "Zoco Check",
        value: summary.zocoCheckActivos,
        note: "Controles activos visibles en V2.",
        tone: "success",
        patch: { procesoCodigo: "zoco-check", soloCompliance: false },
      },
      {
        key: "sin-facturacion",
        title: "Sin facturacion",
        value: summary.sinFacturacionActivos,
        note: "Terminales detectadas sin actividad.",
        tone: "warning",
        patch: { procesoCodigo: "terminal-sin-facturacion", soloCompliance: false },
      },
      {
        key: "recupero",
        title: "Recupero",
        value: summary.recuperoActivos,
        note: "Casos activos de recupero de terminal.",
        tone: "warning",
        patch: { procesoCodigo: "recupero-terminal", soloCompliance: false },
      },
      {
        key: "extraviadas",
        title: "Terminal extraviada",
        value: summary.extraviadasActivas,
        note: "Casos con protocolo intensivo.",
        tone: "danger",
        patch: { procesoCodigo: "terminal-extraviada", soloCompliance: false },
      },
      {
        key: "compliance",
        title: "Derivaciones a Compliance",
        value: summary.compliancePendientes,
        note: "Informativo y de solo lectura dentro de Postventa V2.",
        tone: "danger",
        patch: { soloCompliance: true, scope: "ALL", procesoCodigo: "", estado: "", prioridad: "", responsableUsuarioId: "" },
      },
    ];
  }, [complianceMode, rows, summary]);

  const handleApplyFilters = async (patch = {}) => {
    const nextFilters = {
      ...filters,
      ...patch,
    };
    setFilters(nextFilters);
    await loadData(nextFilters, fechaReferencia);
    window.setTimeout(() => {
      tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const handleReset = async () => {
    const reset = buildDefaultFilters(complianceMode);
    setFilters(reset);
    await loadData(reset, fechaReferencia);
  };

  const refreshDetail = async (item) => {
    const processSlug = item.procesoSlug || getSlugByProcessCode(item.procesoCodigo);
    const [detailResponse, evidencesResponse] = await Promise.all([
      crmPostventaV2Api.obtenerDetalle(processSlug, item.id),
      crmPostventaV2Api.obtenerEvidencias(item.id),
    ]);

    setDetail({
      ...detailResponse,
      instancia: {
        ...detailResponse?.instancia,
        procesoSlug: processSlug,
      },
    });
    setExistingEvidences(evidencesResponse || []);

    return detailResponse;
  };

  const openDetail = async (item) => {
    try {
      setSubmitting(true);
      await refreshDetail(item);
      setModalOpen(true);
    } catch (error) {
      Swal.fire("CRM Postventa V2", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOwnershipAction = async (action, item, body = {}) => {
    try {
      setSubmitting(true);
      const versionToken = body.versionToken || item.versionToken;
      if (action === "take") {
        await crmPostventaV2Api.tomarProceso(item.id, { versionToken, motivo: body.motivo || "Toma desde centro operativo V2." });
      } else if (action === "assign") {
        await crmPostventaV2Api.asignarProceso(item.id, {
          versionToken,
          usuarioId: body.usuarioId,
          motivo: body.motivo || "Asignacion desde centro operativo V2.",
        });
      } else if (action === "release") {
        await crmPostventaV2Api.liberarProceso(item.id, { versionToken, motivo: body.motivo || "Liberacion desde centro operativo V2." });
      }

      await loadData(filters, fechaReferencia);
      if (modalOpen && detail?.instancia?.id === item.id) {
        await refreshDetail({
          ...item,
          procesoCodigo: item.procesoCodigo || detail.instancia.procesoCodigo,
          procesoSlug: item.procesoSlug || detail.instancia.procesoSlug,
        });
      }
    } catch (error) {
      Swal.fire("CRM Postventa V2", error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTake = async (item) => {
    await handleOwnershipAction("take", item);
  };

  const handleRelease = async (item) => {
    const confirm = await Swal.fire({
      title: "Liberar proceso",
      text: "El proceso quedara nuevamente sin responsable.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    await handleOwnershipAction("release", item);
  };

  const handleAssign = async (item) => {
    const usuarioId = await openAssignResponsibleDialog({
      title: "Asignar responsable",
      responsables,
      currentResponsibleId: item.responsableUsuarioId,
      emptyText: "Todavia no hay responsables visibles para asignar desde la grilla.",
    });

    if (!usuarioId) {
      return;
    }

    await handleOwnershipAction("assign", item, { usuarioId });
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) {
      return;
    }

    try {
      setSubmitting(true);
      const refreshedDetail = await refreshDetail(detail.instancia);
      const refreshedInstance = refreshedDetail?.instancia || detail.instancia;
      const processSlug = refreshedInstance.procesoSlug || detail.instancia.procesoSlug;
      const response = await crmPostventaV2Api.ejecutarAccion(processSlug, refreshedInstance.id, {
        ...payload,
        versionToken: refreshedInstance.versionToken || payload.versionToken,
      });
      await Swal.fire("CRM Postventa V2", response?.message || "Accion registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      await loadData(filters, fechaReferencia);
    } catch (error) {
      if (error?.status === 409 && isVersionConflict(error.message)) {
        await refreshDetail(detail.instancia);
        await loadData(filters, fechaReferencia);
        await Swal.fire("CRM Postventa V2", "La gestión fue modificada por otro usuario. Actualizamos el detalle para que puedas intentar de nuevo.", "info");
        return;
      }

      if (error?.status === 409) {
        await refreshDetail(detail.instancia);
      }

      Swal.fire("CRM Postventa V2", error.message, error?.status === 409 ? "warning" : "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={`crm-postventa-panel crm-postventa-v2-alert-center ${darkMode ? "is-dark" : ""}`}>
      <div className="crm-postventa-panel-title">
        <div>
          <h3>Centro operativo</h3>
          <div className="crm-postventa-subtle">
            Asignacion de responsables, filtros operativos y vista informativa de derivaciones a Compliance.
          </div>
        </div>
        <div className="crm-postventa-inline-badges">
          <span className="crm-postventa-chip">Total {counter.total || 0}</span>
          <span className="crm-postventa-chip">Nuevas {counter.nuevas || 0}</span>
          <span className="crm-postventa-chip">Vencidas {counter.vencidas || 0}</span>
          <span className="crm-postventa-chip">Criticas {counter.criticas || 0}</span>
          {permissions.puedeVerCompliance ? <span className="crm-postventa-chip">Compliance {counter.compliance || 0}</span> : null}
        </div>
      </div>

      <div className="crm-postventa-actions-row mb-3">
        <div className="crm-postventa-range crm-postventa-alert-filters">
          <Form.Control
            type="date"
            className="crm-postventa-filter-control crm-postventa-filter-date"
            value={fechaReferencia}
            onChange={(event) => setFechaReferencia(event.target.value)}
          />
          <ButtonGroup aria-label="Alcance operativo">
            {scopeOptions.map((option) => (
              <Button
                key={option.value}
                variant={filters.scope === option.value ? "success" : "outline-secondary"}
                onClick={() => setFilters((prev) => ({ ...prev, scope: option.value }))}
              >
                {option.label}
              </Button>
            ))}
          </ButtonGroup>
          <Form.Select
            className="crm-postventa-filter-control crm-postventa-filter-select"
            value={filters.procesoCodigo}
            onChange={(event) => setFilters((prev) => ({ ...prev, procesoCodigo: event.target.value }))}
          >
            {processOptions.map((option) => (
              <option key={option.value || "all-process"} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
          <Form.Select
            className="crm-postventa-filter-control crm-postventa-filter-select"
            value={filters.prioridad}
            onChange={(event) => setFilters((prev) => ({ ...prev, prioridad: event.target.value }))}
          >
            {priorityOptions.map((option) => (
              <option key={option.value || "all-priority"} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
          <Form.Select
            className="crm-postventa-filter-control crm-postventa-filter-select"
            value={filters.estado}
            onChange={(event) => setFilters((prev) => ({ ...prev, estado: event.target.value }))}
          >
            {generalStateOptions.map((option) => (
              <option key={option.value || "all-state"} value={option.value}>{option.label}</option>
            ))}
          </Form.Select>
          <Form.Select
            className="crm-postventa-filter-control crm-postventa-filter-select"
            value={filters.responsableUsuarioId}
            onChange={(event) => setFilters((prev) => ({ ...prev, responsableUsuarioId: event.target.value }))}
          >
            <option value="">Todos los responsables</option>
            {responsables.map((item) => (
              <option key={item.usuarioId} value={item.usuarioId}>
                {item.nombre} ({item.total})
              </option>
            ))}
          </Form.Select>
          <Form.Control
            type="search"
            className="crm-postventa-filter-control crm-postventa-filter-search"
            placeholder="Buscar por CUIT, comercio, terminal o estado"
            value={filters.texto}
            onChange={(event) => setFilters((prev) => ({ ...prev, texto: event.target.value }))}
          />
          <Form.Check
            type="switch"
            label="Solo vencidos"
            checked={filters.soloVencidos}
            onChange={(event) => setFilters((prev) => ({ ...prev, soloVencidos: event.target.checked }))}
          />
          {permissions.puedeVerCompliance ? (
            <Form.Check
              type="switch"
              label="Solo Compliance"
              checked={filters.soloCompliance}
              onChange={(event) => setFilters((prev) => ({ ...prev, soloCompliance: event.target.checked }))}
            />
          ) : null}
          <Button className="crm-postventa-filter-button" variant="outline-success" onClick={() => loadData(filters, fechaReferencia)} disabled={loading}>
            Actualizar
          </Button>
          <Button variant="outline-secondary" onClick={handleReset} disabled={loading}>
            Limpiar
          </Button>
        </div>
      </div>

      <div className="crm-postventa-card-grid crm-postventa-alert-kpis crm-postventa-v2-summary-grid">
        {cardItems.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`crm-postventa-card crm-postventa-v2-card-button crm-postventa-v2-tone-${item.tone} ${darkMode ? "is-dark" : ""}`}
            onClick={() => handleApplyFilters(item.patch)}
          >
            <span className="crm-postventa-card-label">{item.title}</span>
            <div className="crm-postventa-card-value">{item.value || 0}</div>
            <p className="crm-postventa-card-note mb-0">{item.note}</p>
          </button>
        ))}
      </div>

      <div className="crm-postventa-insights crm-postventa-alert-insights mb-3">
        <article className="crm-postventa-insight">
          <span className="crm-postventa-card-label">Resumen V2</span>
          <strong>{summary.totalProcesosActivos || 0} procesos activos visibles</strong>
        </article>
        <article className="crm-postventa-insight">
          <span className="crm-postventa-card-label">Asignacion</span>
          <strong>{summary.procesosSinResponsable || 0} sin responsable</strong>
        </article>
        <article className="crm-postventa-insight">
          <span className="crm-postventa-card-label">Alcance actual</span>
          <strong>
            {complianceMode
              ? "Compliance solo lectura"
              : permissions.puedeAdministrarPostventa
                ? "Administrador"
                : "Postventa general"}
          </strong>
        </article>
      </div>

      {!!notifications.length ? (
        <div className="crm-postventa-card-grid crm-postventa-alert-kpis mb-3">
          {notifications.map((item) => (
            <button
              key={`${item.procesoId}-${item.tipoEvento}`}
              type="button"
              className={`crm-postventa-card crm-postventa-v2-card-button ${darkMode ? "is-dark" : ""}`}
              onClick={() => {
                const row = rows.find((entry) => entry.id === item.procesoId);
                if (row) {
                  openDetail(row);
                }
              }}
            >
              <span className="crm-postventa-card-label">{item.titulo}</span>
              <div className="crm-postventa-card-value">{item.procesoCodigo}</div>
              <p className="crm-postventa-card-note mb-0">{item.descripcion}</p>
              <p className="crm-postventa-card-note mb-0">{item.fechaReferencia}</p>
            </button>
          ))}
        </div>
      ) : null}

      <div ref={tableRef} className="crm-postventa-v2-alert-table-anchor">
        {loading ? (
          <div className="py-4 text-center">
            <Spinner animation="border" size="sm" className="me-2" />
            Cargando alertas V2...
          </div>
        ) : rows.length ? (
          <ProcessQueueTable
            rows={rows}
            onManage={openDetail}
            onTake={handleTake}
            onAssign={handleAssign}
            onRelease={handleRelease}
            loading={submitting}
            includeProcess
          />
        ) : (
          <Alert variant="secondary" className="mb-0">
            No hay resultados V2 para los filtros actuales.
          </Alert>
        )}
      </div>

      <ProcessActionModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        onSubmit={handleSubmitAction}
      />
    </section>
  );
}
